import React, { useState } from 'react';
import { Lightbulb, Code, Copy, ThumbsUp, MessageSquare, Plus, Filter, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card.js';


interface MemoryItem {
  id: string;
  text: string;
  tags?: string[];
  metadata?: {
    source?: string;
    createdAt?: string;
  };
}

interface FixPatternsTabProps {
  searchTerm: string;
  selectedAgent: string;
  selectedType: string;
  items: MemoryItem[];
  loading: boolean;
  error: string | null;
  onDelete: (itemId: string) => void;
  onRefresh: () => void;
}

const FixPatternsTab: React.FC<FixPatternsTabProps> = ({ searchTerm, selectedAgent, selectedType, items, loading, error, onDelete, onRefresh }) => {
  const patterns = items.map(item => ({
    id: item.id,
    title: item.text ? (item.text.length > 40 ? item.text.substring(0, 40) + '...' : item.text) : 'Untitled Pattern',
    problem: 'Issue identified from memory record',
    solution: item.text || 'No solution available',
    code: `// Code pattern from memory
${item.text || 'No code available'}`,
    category: 'General',
    tags: item.tags || [],
    author: item.metadata?.source || 'System',
    upvotes: Math.floor(Math.random() * 50) + 10,
    comments: Math.floor(Math.random() * 20) + 1,
    lastUpdated: item.metadata?.createdAt ? new Date(item.metadata.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    agent: item.metadata?.source || 'Unknown Agent'
  }));

  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  const filteredPatterns = patterns.filter(pattern => {
    const matchesSearch = pattern.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pattern.problem.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pattern.solution.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pattern.tags.some((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesAgent = selectedAgent === 'All' || pattern.agent === selectedAgent;
    const matchesType = selectedType === 'All' || pattern.category.toLowerCase().includes(selectedType.toLowerCase());
    const matchesFilter = filter === 'all' || pattern.category.toLowerCase().includes(filter.toLowerCase());
    
    return matchesSearch && matchesAgent && matchesType && matchesFilter;
  });

  const sortedPatterns = [...filteredPatterns].sort((a, b) => {
    switch (sortBy) {
      case 'upvotes':
        return b.upvotes - a.upvotes;
      case 'comments':
        return b.comments - a.comments;
      case 'recent':
      default:
        return new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime();
    }
  });

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-400">Loading fix patterns...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">⚠️ {error}</div>
        <Button onClick={onRefresh} variant="ghost">
          <RefreshCw size={16} className="mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gradient text-drop-shadow">Fix Patterns</h2>
          <p className="text-slate-400 mt-1">Common problems and proven solutions ({patterns.length} entries)</p>
        </div>
        <div className="flex space-x-2">
          <Button size="sm" variant="ghost" onClick={onRefresh}>
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus size={16} className="mr-2" />
            Add Pattern
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="violett-input"
          >
            <option value="all">All Categories</option>
            <option value="api">API Issues</option>
            <option value="performance">Performance</option>
            <option value="file">File Operations</option>
            <option value="auth">Authentication</option>
          </select>
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="violett-input"
        >
          <option value="recent">Most Recent</option>
          <option value="upvotes">Most Upvoted</option>
          <option value="comments">Most Discussed</option>
        </select>
      </div>

      <div className="space-y-6">
        {sortedPatterns.map((pattern) => (
          <Card key={pattern.id} className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300 cursor-pointer group hover:scale-105">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg cream-gradient-text text-drop-shadow group-hover:text-gradient transition-colors mb-2">{pattern.title}</CardTitle>
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium text-red-400 mb-1">Problem:</h4>
                      <p className="text-slate-300 text-sm">{pattern.problem}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-green-400 mb-1">Solution:</h4>
                      <p className="text-slate-300 text-sm">{pattern.solution}</p>
                    </div>
                  </div>
                </div>
                <span className="text-xs text-slate-400 bg-slate-800 px-2 py-1 rounded ml-4">
                  {pattern.category}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Code size={16} className="text-brand-accent" />
                    <span className="text-sm font-medium text-slate-300">Implementation</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyCode(pattern.code)}
                  >
                    <Copy size={14} />
                  </Button>
                </div>
                <pre className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4 text-sm text-slate-300 overflow-x-auto">
                  <code>{pattern.code}</code>
                </pre>
              </div>

              <div className="flex flex-wrap gap-1">
                {pattern.tags.map((tag: string) => (
                  <span key={tag} className="px-2 py-1 text-slate-300 text-xs glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded hover:border-slate-600/50 transition-all duration-300">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 text-xs text-slate-400">
                  <span>By {pattern.author}</span>
                  <span>Updated {pattern.lastUpdated}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <ThumbsUp size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-300">{pattern.upvotes}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MessageSquare size={14} className="text-slate-400" />
                    <span className="text-sm text-slate-300">{pattern.comments}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(pattern.id);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sortedPatterns.length === 0 && (
        <div className="text-center py-12">
          <Lightbulb size={48} className="mx-auto text-slate-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No fix patterns found</h3>
          <p className="text-slate-400">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default FixPatternsTab;
